import React, { useState, useEffect } from "react";
import { api } from "../shared/utilities/api";
import apiConfigs from "../shared/utilities/apiConfigs";
import ReportFooter from "../../frame/GetReportFooter";

const MAX_FILE_COUNT = 5;

// ─── Font injection ────────────────────────────────────────────────────────────
const injectFont = () => {
	if (document.getElementById("file-uploader-font")) return;
	const link = document.createElement("link");
	link.id = "file-uploader-font";
	link.rel = "stylesheet";
	link.href = "https://fonts.googleapis.com/css2?family=Manrope:wght@600;700;800&family=Inter:wght@400;500;600&display=swap";
	document.head.appendChild(link);
};

// ─── Inline styles — matches split card from UploadPatientReportForm ───────────
const injectStyles = () => {
	if (document.getElementById("file-uploader-styles")) return;
	const style = document.createElement("style");
	style.id = "file-uploader-styles";
	style.textContent = `
		.fu-wrap, .fu-wrap *, .fu-wrap *::before, .fu-wrap *::after {
			box-sizing: border-box;
		}
		.fu-wrap {
			font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Arial, sans-serif;
			width: 100%;
			max-width: 100%;
			overflow-x: hidden;
		}

		/* ── Split card — same structure as step 1 ── */
		.fu-card {
			width: 100%;
			display: grid;
			grid-template-columns: 200px 1fr;
			background: #fff;
			border-radius: 0 0 20px 20px;
			border: 1px solid rgba(0,0,0,0.06);
			border-top: none;
			box-shadow: 0 2px 4px rgba(0,0,0,0.04), 0 20px 48px -20px rgba(0,0,0,0.14);
			overflow: hidden;
		}

		/* ── Left orange panel ── */
		.fu-card__left {
			background: linear-gradient(155deg, #F4A96A 0%, #E47029 50%, #B94D1A 100%);
			padding: 32px 22px;
			display: flex;
			flex-direction: column;
			justify-content: space-between;
			position: relative;
			overflow: hidden;
		}
		.fu-card__left::before {
			content: '';
			position: absolute;
			width: 200px; height: 200px;
			border-radius: 50%;
			border: 1px solid rgba(255,255,255,0.14);
			right: -80px; top: -80px;
		}
		.fu-card__left::after {
			content: '';
			position: absolute;
			width: 140px; height: 140px;
			border-radius: 50%;
			border: 1px solid rgba(255,255,255,0.12);
			left: -50px; bottom: -50px;
		}
		.fu-card__left-top { position: relative; z-index: 1; }
		.fu-card__left-icon {
			width: 44px; height: 44px;
			border-radius: 12px;
			background: rgba(255,255,255,0.2);
			display: flex;
			align-items: center;
			justify-content: center;
			margin-bottom: 16px;
		}
		.fu-card__left-title {
			font-family: 'Manrope', sans-serif;
			font-size: 16px;
			font-weight: 800;
			color: #fff;
			letter-spacing: -0.01em;
			line-height: 1.3;
			margin-bottom: 10px;
		}
		.fu-card__left-sub {
			font-size: 12px;
			color: rgba(255,255,255,0.78);
			line-height: 1.55;
		}
		.fu-card__left-bottom { position: relative; z-index: 1; }
		.fu-card__left-steps {
			display: flex;
			flex-direction: column;
			gap: 10px;
		}
		.fu-step {
			display: flex;
			align-items: center;
			gap: 8px;
			font-size: 11.5px;
			color: rgba(255,255,255,0.55);
		}
		.fu-step.done { color: rgba(255,255,255,0.65); }
		.fu-step.active { color: #fff; font-weight: 600; }
		.fu-step-num {
			width: 18px; height: 18px;
			border-radius: 50%;
			background: rgba(255,255,255,0.2);
			display: flex;
			align-items: center;
			justify-content: center;
			font-size: 9px;
			font-weight: 700;
			flex-shrink: 0;
		}
		.fu-step.active .fu-step-num { background: #fff; color: #E47029; }
		.fu-step.done .fu-step-num {
			background: rgba(255,255,255,0.35);
			color: #fff;
		}

		/* ── Right panel ── */
		.fu-card__right { padding: 32px 28px; min-width: 0; }
		.fu-card__right-label {
			font-size: 11px;
			font-weight: 700;
			color: #E47029;
			text-transform: uppercase;
			letter-spacing: 0.07em;
			margin-bottom: 8px;
		}
		.fu-card__right-title {
			font-family: 'Manrope', sans-serif;
			font-size: 18px;
			font-weight: 800;
			color: #18181A;
			letter-spacing: -0.01em;
			margin-bottom: 6px;
		}
		.fu-card__right-sub {
			font-size: 13px;
			color: #6B6760;
			line-height: 1.55;
			margin-bottom: 22px;
		}
		.fu-card__right-sub strong { color: #E47029; font-weight: 700; }

		/* upload zone */
		.fu-dropzone {
			border: 2px dashed rgba(228,112,41,0.35);
			border-radius: 12px;
			padding: 20px 16px;
			text-align: center;
			background: #FDFAF8;
			margin-bottom: 16px;
			transition: border-color 0.15s, background 0.15s;
			cursor: pointer;
			position: relative;
		}
		.fu-dropzone:hover { border-color: #E47029; background: #FEF0E7; }
		.fu-dropzone input[type="file"] {
			position: absolute;
			inset: 0;
			opacity: 0;
			cursor: pointer;
			width: 100%;
			height: 100%;
		}
		.fu-dropzone__icon {
			width: 40px; height: 40px;
			border-radius: 10px;
			background: #FEF0E7;
			color: #E47029;
			display: flex;
			align-items: center;
			justify-content: center;
			margin: 0 auto 10px;
		}
		.fu-dropzone__title {
			font-family: 'Manrope', sans-serif;
			font-size: 13.5px;
			font-weight: 700;
			color: #18181A;
			margin-bottom: 4px;
		}
		.fu-dropzone__sub { font-size: 11.5px; color: #9A9A9F; }
		.fu-dropzone__sub span { color: #E47029; font-weight: 600; }
		.fu-dropzone.disabled {
			opacity: 0.5;
			cursor: not-allowed;
			pointer-events: none;
		}

		/* file count indicator */
		.fu-count {
			display: flex;
			align-items: center;
			justify-content: space-between;
			margin-bottom: 12px;
		}
		.fu-count__label { font-size: 11.5px; color: #9A9A9F; }
		.fu-count__badge {
			font-size: 11px;
			font-weight: 700;
			padding: 2px 8px;
			border-radius: 20px;
			background: #FEF0E7;
			color: #E47029;
		}

		/* file list */
		.fu-file-list { margin-bottom: 16px; }
		.fu-file-item {
			display: flex;
			align-items: center;
			gap: 10px;
			padding: 10px 12px;
			border: 1.5px solid rgba(0,0,0,0.08);
			border-radius: 10px;
			margin-bottom: 8px;
			background: #FAFAFA;
			transition: border-color 0.15s;
			min-width: 0;
		}
		.fu-file-item:hover { border-color: #F5D5C0; }
		.fu-file-item__icon {
			width: 32px; height: 32px;
			border-radius: 8px;
			background: #FEF0E7;
			color: #E47029;
			display: flex;
			align-items: center;
			justify-content: center;
			flex-shrink: 0;
		}
		.fu-file-item__name {
			font-size: 13px;
			font-weight: 500;
			color: #18181A;
			flex: 1;
			min-width: 0;
			overflow: hidden;
			text-overflow: ellipsis;
			white-space: nowrap;
		}
		.fu-file-item__del {
			width: 28px; height: 28px;
			border-radius: 7px;
			border: none;
			background: transparent;
			cursor: pointer;
			color: #C0BDB8;
			display: flex;
			align-items: center;
			justify-content: center;
			flex-shrink: 0;
			transition: background 0.15s, color 0.15s;
		}
		.fu-file-item__del:hover { background: #FCEBEB; color: #E53E3E; }

		/* buttons */
		.fu-btn {
			width: 100%;
			height: 48px;
			border: none;
			border-radius: 10px;
			background: #E47029;
			color: #fff;
			font-family: 'Manrope', sans-serif;
			font-size: 14.5px;
			font-weight: 700;
			cursor: pointer;
			display: flex;
			align-items: center;
			justify-content: center;
			gap: 8px;
			transition: background 0.15s, transform 0.1s;
			letter-spacing: 0.01em;
		}
		.fu-btn:hover:not(:disabled) { background: #B94D1A; }
		.fu-btn:active:not(:disabled) { transform: scale(0.99); }
		.fu-btn:disabled { background: #E5C9B0; cursor: not-allowed; }
		.fu-security {
			display: flex;
			align-items: center;
			gap: 6px;
			margin-top: 12px;
			font-size: 11px;
			color: #C0BDB8;
			justify-content: center;
		}

		/* ── Success state ── */
		.fu-success {
			display: flex;
			flex-direction: column;
			align-items: center;
			justify-content: center;
			padding: 48px 32px;
			text-align: center;
		}
		.fu-success__icon {
			width: 64px; height: 64px;
			border-radius: 50%;
			background: #E8F5E9;
			border: 2px solid #C8E6C9;
			color: #4CAF50;
			display: flex;
			align-items: center;
			justify-content: center;
			margin-bottom: 20px;
		}
		.fu-success__title {
			font-family: 'Manrope', sans-serif;
			font-size: 20px;
			font-weight: 800;
			color: #18181A;
			margin-bottom: 8px;
			letter-spacing: -0.01em;
		}
		.fu-success__sub { font-size: 14px; color: #6B6760; line-height: 1.6; }

		/* ── Responsive ── */
		@media (max-width: 680px) {
			.fu-card {
				grid-template-columns: 1fr;
				border-radius: 0 0 16px 16px;
			}
			.fu-card__left {
				padding: 20px;
				min-height: auto;
			}
			.fu-card__left-steps {
				flex-direction: row;
				gap: 14px;
				flex-wrap: wrap;
			}
			.fu-card__left-top { margin-bottom: 14px; }
			.fu-card__left-icon { margin-bottom: 10px; }
			.fu-card__left-title { font-size: 15px; }
			.fu-card__right { padding: 24px 20px; }
		}

		@media (max-width: 420px) {
			.fu-card__left { padding: 16px; }
			.fu-card__right { padding: 18px 16px; }
			.fu-card__right-title { font-size: 16px; }
			.fu-dropzone { padding: 16px 12px; }
			.fu-count {
				flex-wrap: wrap;
				gap: 6px;
			}
			.fu-file-item {
				flex-wrap: wrap;
				row-gap: 8px;
			}
			.fu-file-item__name {
				width: 100%;
				order: 1;
				white-space: normal;
				word-break: break-word;
				flex-basis: 100%;
			}
			.fu-file-item__del {
				order: 2;
				margin-left: auto;
			}
			.fu-success { padding: 36px 20px; }
			.fu-success__icon { width: 52px; height: 52px; }
			.fu-success__title { font-size: 17px; }
		}
	`;
	document.head.appendChild(style);
};

// ─── Icons ─────────────────────────────────────────────────────────────────────
const UploadIcon = ({ size = 20, color = "currentColor" }) => (
	<svg viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" width={size} height={size} aria-hidden="true">
		<path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
		<polyline points="17 8 12 3 7 8"/>
		<line x1="12" y1="3" x2="12" y2="15"/>
	</svg>
);

const FileIcon = () => (
	<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="16" height="16" aria-hidden="true">
		<path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
		<polyline points="14 2 14 8 20 8"/>
	</svg>
);

const DeleteIcon = () => (
	<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="14" height="14" aria-hidden="true">
		<polyline points="3 6 5 6 21 6"/>
		<path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/>
		<path d="M10 11v6M14 11v6"/>
		<path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/>
	</svg>
);

const CheckIcon = () => (
	<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" width="28" height="28" aria-hidden="true">
		<path d="M20 6 9 17l-5-5"/>
	</svg>
);

const SendIcon = () => (
	<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="16" height="16" aria-hidden="true">
		<line x1="22" y1="2" x2="11" y2="13"/>
		<polygon points="22 2 15 22 11 13 2 9 22 2"/>
	</svg>
);

const LockIcon = () => (
	<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="12" height="12" aria-hidden="true">
		<rect x="4" y="10" width="16" height="10" rx="2"/>
		<path d="M8 10V7a4 4 0 0 1 8 0v3"/>
	</svg>
);

// ─── Main component ────────────────────────────────────────────────────────────
const FileUploader = ({ orgName, hashedId, sessionId, hin, orgId, userId }) => {
	const [loading, setLoading] = useState(false);
	const [isUploaded, setIsUploaded] = useState(false);
	const [files, setFiles] = useState([]);

	useEffect(() => {
		injectFont();
		injectStyles();
	}, []);

	const handleFileChange = (e) => {
		const selectedFiles = e.target.files;
		const totalFiles = selectedFiles.length + files.length;

		const validFileTypes = [
			"image/jpeg",
			"image/png",
			"application/pdf",
			"application/msword",
			"application/vnd.openxmlformats-officedocument.wordprocessingml.document",
			"text/plain",
			"application/rtf",
		];

		if (!selectedFiles || selectedFiles.length === 0) {
			alert("Please select your files to upload.");
			return;
		}
		if (totalFiles > MAX_FILE_COUNT) {
			alert(`You can only upload a total of ${MAX_FILE_COUNT} files.`);
			return;
		}

		let allFilesAreValid = true;
		for (let i = 0; i < selectedFiles.length; i++) {
			const selectedFile = selectedFiles[i];
			const fileType = selectedFile.type;
			const fileExtension = selectedFile.name.split(".").pop().toLowerCase();
			if (!(validFileTypes.includes(fileType) && fileExtension !== "rtf")) {
				allFilesAreValid = false;
				break;
			}
		}
		if (!allFilesAreValid) {
			alert("Invalid file type. Please upload a JPEG, PNG, PDF, Word document, or RTF file.");
			return;
		}

		let hasDuplicateFiles = false;
		for (let i = 0; i < selectedFiles.length; i++) {
			if (files.some((file) => file.name === selectedFiles[i].name)) {
				hasDuplicateFiles = true;
				break;
			}
		}
		if (hasDuplicateFiles) {
			alert("Duplicate files are selected.");
			return;
		}

		for (let i = 0; i < selectedFiles.length; i++) {
			const selectedFile = selectedFiles[i];
			setFiles((curr) => [...curr, { file: selectedFile, name: selectedFile.name }]);
		}
	};

	const handleDelete = (targetIndex) => {
		setFiles((curr) => curr.filter((_, index) => index !== targetIndex));
	};

	const toBase64 = (file) => {
		return new Promise((resolve, reject) => {
			const reader = new FileReader();
			reader.readAsDataURL(file);
			reader.onload = () => resolve(reader.result);
			reader.onerror = (error) => {
				console.error("File reading error:", error);
				reject("Error reading file. Please try again.");
			};
		});
	};

	const handleSubmit = async (e) => {
		e.preventDefault();
		if (!files.length) {
			alert("Please upload a file before submitting.");
			return;
		}
		const confirmation = window.confirm("Are you sure you want to share these files?");
		if (!confirmation) return;

		setLoading(true);
		try {
			const base64Promises = files.map((file) => toBase64(file.file));
			const base64Files = await Promise.all(base64Promises);

			const uploadPromises = files.map((file, index) =>
				api(
					apiConfigs.baseOption("POST", "file/uploadFileToS3WithUrl", {
						data: {
							orgName, orgId, userId, hashedId, sessionId, hin,
							file: { name: file.name, document: base64Files[index] },
						},
					})
				)
			);
			await Promise.all(uploadPromises);
			setIsUploaded(true);
		} catch (error) {
			console.error("Error during file upload:", error);
			alert("Failed to upload the file. Please try again later.");
		}
		setLoading(false);
	};

	return (
		<>
			<div className="fu-wrap">
				<div className="fu-card">

					{/* ── Left orange panel — step 2 active ── */}
					<div className="fu-card__left">
						<div className="fu-card__left-top">
							<div className="fu-card__left-icon">
								<UploadIcon size={22} color="#fff" />
							</div>
							<div className="fu-card__left-title">Upload Your Reports</div>
							<div className="fu-card__left-sub">
								Securely share your medical files with your clinic.
							</div>
						</div>
						<div className="fu-card__left-bottom">
							<div className="fu-card__left-steps">
								<div className="fu-step done">
									<div className="fu-step-num">✓</div>
									Verify identity
								</div>
								<div className={`fu-step ${isUploaded ? "done" : "active"}`}>
									<div className="fu-step-num">{isUploaded ? "✓" : "2"}</div>
									Upload files
								</div>
								<div className={`fu-step ${isUploaded ? "active" : ""}`}>
									<div className="fu-step-num">3</div>
									Done
								</div>
							</div>
						</div>
					</div>

					{/* ── Right panel ── */}
					<div className="fu-card__right">
						{isUploaded ? (
							/* ── Success state ── */
							<div className="fu-success">
								<div className="fu-success__icon">
									<CheckIcon />
								</div>
								<div className="fu-success__title">Files Uploaded!</div>
								<div className="fu-success__sub">
									Thank you for submitting your files.<br />
									They have been uploaded successfully.
								</div>
							</div>
						) : (
							<>
								<div className="fu-card__right-label">Step 2 of 3</div>
								<div className="fu-card__right-title">Upload your files</div>
								<div className="fu-card__right-sub">
									<strong>{orgName}</strong> has requested that you upload your files. Please securely submit them below.
								</div>

								{/* Drop zone */}
								<div className={`fu-dropzone${files.length >= MAX_FILE_COUNT ? " disabled" : ""}`}>
									<input
										type="file"
										accept=".jpeg,.jpg,.png,.pdf,.doc,.docx,.txt,.rtf"
										onChange={handleFileChange}
										multiple
										disabled={files.length >= MAX_FILE_COUNT}
									/>
									<div className="fu-dropzone__icon">
										<UploadIcon size={20} color="#E47029" />
									</div>
									<div className="fu-dropzone__title">Select your files</div>
									<div className="fu-dropzone__sub">
										<span>Click to browse</span> · JPEG, PNG, PDF, Word, TXT
									</div>
								</div>

								{/* File count */}
								{files.length > 0 && (
									<div className="fu-count">
										<span className="fu-count__label">{files.length} file{files.length !== 1 ? "s" : ""} selected</span>
										<span className="fu-count__badge">{files.length} / {MAX_FILE_COUNT}</span>
									</div>
								)}

								{/* File list */}
								{files.length > 0 && (
									<div className="fu-file-list">
										{files.map((file, idx) => (
											<div key={file.name} className="fu-file-item">
												<div className="fu-file-item__icon">
													<FileIcon />
												</div>
												<span className="fu-file-item__name">{file.name}</span>
												<button
													type="button"
													className="fu-file-item__del"
													onClick={() => handleDelete(idx)}
													aria-label={`Remove ${file.name}`}
												>
													<DeleteIcon />
												</button>
											</div>
										))}
									</div>
								)}

								{/* Submit button */}
								{files.length > 0 && (
									<>
										<button
											className="fu-btn"
											onClick={handleSubmit}
											disabled={loading}
										>
											{loading
												? <><span style={{width:16,height:16,border:"2px solid rgba(255,255,255,0.4)",borderTopColor:"#fff",borderRadius:"50%",animation:"fu-spin 0.7s linear infinite",display:"inline-block"}} />Uploading...</>
												: <><SendIcon />Save &amp; Submit</>
											}
										</button>
										<style>{`@keyframes fu-spin { to { transform: rotate(360deg); } }`}</style>
										<div className="fu-security">
											<LockIcon />
											256-bit encrypted · PHIPA compliant
										</div>
									</>
								)}
							</>
						)}
					</div>

				</div>
			</div>

		</>
	);
};

export default FileUploader;